import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { riderWork } from '../api/riderWork';
import PackagePhotoCapture from '../components/PackagePhotoCapture';
import ClientParcelPhoto from '../components/driver/ClientParcelPhoto';
import DriverJobState from '../components/driver/DriverJobState';
import PackageProblemReport from '../components/driver/PackageProblemReport';
import { jobPath, useDriverJob } from '../components/driver/useDriverJob';
import { IconCheck } from '../components/icons/LineIcons';
import { isPackagePhotoSrc } from '../lib/packagePhoto';
import { dataUrlToBlob, isOnDropOffLeg, jobFromApi } from '../lib/riderWork';
import { useGoBack } from '../lib/useGoBack';
import './driverDelivery.css';

const CHK = ['Package matches description', 'Package is sealed properly', 'Correct package size'];

const GIVEN_BACK = 'Job given back. The next rider is being asked.';

function Back() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
      <path
        d="M15.5 19.5L8 12l7.5-7.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function LocPin() {
  return (
    <svg className="pu-icoG" viewBox="0 0 24 24" width="40" height="40" fill="none" aria-hidden>
      <path
        d="M12 2.2a4.2 4.2 0 0 0-3.5 1.5L2.1 9.1a.6.6 0 0 0 .1.8L12 22.2l9.6-10.1a.6.6 0 0 0 0-1.2L15.4 3.7A4.1 4.1 0 0 0 12 2.2Z"
        fill="#ec6c23"
      />
      <circle cx="12" cy="8.2" r="1.2" fill="#fff" />
    </svg>
  );
}

export default function DriverPickupConfirmPage() {
  const navigate = useNavigate();
  const { order, status: jobStatus, error: jobError, reload: reloadJob } = useDriverJob();
  const o = useMemo(() => order || {}, [order]);
  const [checks, setChecks] = useState(() => ({}));
  const [driverPhoto, setDriverPhoto] = useState(() =>
    isPackagePhotoSrc(o.driverPackagePhotoDataUrl) ? o.driverPackagePhotoDataUrl : '',
  );
  const [photoErr, setPhotoErr] = useState('');
  const [saving, setSaving] = useState(false);
  // 'checklist', 'report' (something is wrong with the package) or 'collect'
  // (reported, and the rider collects it anyway: the checklist cannot be true).
  const [mode, setMode] = useState('checklist');
  // Opened fresh (a reload, a notification) there is no screen before this one.
  const goBack = useGoBack(jobPath('active-delivery', o));

  const toggleC = (label) => {
    setChecks((c) => ({ ...c, [label]: !c[label] }));
  };
  // A problem reported earlier: the rider came back with the decision to make.
  const reportedBefore = Array.isArray(o.problems) && o.problems.length > 0;
  const view = mode === 'checklist' && reportedBefore ? 'decide' : mode;
  const mayReport = !isOnDropOffLeg(o) && Boolean(o.mayReportProblem);
  // On InGo the client's code proves the drop-off, so the pickup asks for none.
  const can = (view === 'collect' || CHK.every((c) => checks[c])) && Boolean(driverPhoto);
  const next = useCallback(async () => {
    if (!can || saving) return;
    setPhotoErr('');
    setSaving(true);
    // The same record as the navigation screen's pickup: the parcel's photo,
    // kept by InGo, and the job on its drop-off leg on the server. A job
    // already picked up (the rider came back to this screen) is not sent twice.
    let picked = o;
    if (!isOnDropOffLeg(o)) {
      try {
        const job = await riderWork.pickUp(o.deliveryId, dataUrlToBlob(driverPhoto), 'package.jpg');
        picked = { ...o, ...(job ? jobFromApi(job) : { status: 'picked_up' }) };
      } catch (e) {
        setSaving(false);
        setPhotoErr(e?.message || 'Could not save the pickup. Check your connection and try again.');
        return;
      }
    }
    setSaving(false);
    navigate(jobPath('navigation', o), {
      replace: true,
      state: {
        order: { ...picked, driverPackagePhotoDataUrl: driverPhoto },
        pickup: o.from,
        dropoff: o.to,
        dest: o.to,
        navLeg: 'toDropoff',
        phase: 'dropoff',
        fromPickup: true,
      },
    });
  }, [can, driverPhoto, navigate, o, saving]);

  if (jobStatus !== 'ready') {
    return <DriverJobState status={jobStatus} error={jobError} label="pickup" onRetry={reloadJob} />;
  }

  return (
    <div className="pu-page dd" role="main" aria-label="Confirm pickup">
      <header className="pu-h" style={{ position: 'relative' }}>
        <button type="button" className="pu-bk" onClick={goBack} aria-label="Back">
          <Back />
        </button>
        <h1>Confirm Pickup</h1>
      </header>
      <div
        className="pu-m"
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg
          viewBox="0 0 120 80"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          aria-hidden
        >
          <rect width="120" height="80" fill="url(#g0)" />
          <defs>
            <linearGradient id="g0" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#5a6a7a" />
              <stop offset="1" stopColor="#3a4a5a" />
            </linearGradient>
          </defs>
        </svg>
        <div className="pu-pinPulse" style={{ position: 'relative', zIndex: 1 }} aria-hidden />
      </div>
      <div className="pu-c">
        <LocPin />
        <p className="pu-ib">You are at pickup location</p>
        <p className="pu-ia2">{o.from}</p>
      </div>
      <div className="pu-s">
        {o.hasPackagePhoto ? <ClientParcelPhoto deliveryId={o.deliveryId} /> : null}
        {view === 'report' || view === 'decide' ? (
          <PackageProblemReport
            deliveryId={o.deliveryId}
            customerPhone={o.customerPhone}
            reported={view === 'decide'}
            onCollectAnyway={() => setMode('collect')}
            onHandedBack={() => navigate('/driver/home', { replace: true, state: { toast: GIVEN_BACK } })}
            onCancel={() => setMode('checklist')}
          />
        ) : (
          <>
            {view === 'collect' ? (
              <p className="pu-ia2" role="status" style={{ textAlign: 'center' }}>
                You reported a problem with this package. The client and the office can see it.
              </p>
            ) : (
              <>
                <h2 className="pu-secL" style={{ marginTop: 0 }}>
                  Confirm package details
                </h2>
                <div className="pu-chL" role="list">
                  {CHK.map((label) => (
                    <button type="button" key={label} className="pu-chI" onClick={() => toggleC(label)}>
                      <span className={checks[label] ? 'pu-cb pu-cb--on' : 'pu-cb'} aria-hidden>
                        {checks[label] ? <IconCheck size={11} strokeWidth={3} /> : null}
                      </span>
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}
            <div style={{ maxWidth: '20rem', width: '100%', margin: '0.15rem auto 0.45rem' }}>
              <PackagePhotoCapture
                compact
                required
                value={driverPhoto}
                onChange={(url) => {
                  setPhotoErr('');
                  setDriverPhoto(url || '');
                }}
                label="Your package photo"
                hint="Take or upload a photo of the parcel you are picking up."
              />
            </div>
            {photoErr ? (
              <p className="pu-ia2" role="alert" style={{ color: '#b91c1c', textAlign: 'center' }}>
                {photoErr}
              </p>
            ) : null}
            <button type="button" className="pu-sub" onClick={next} disabled={!can || saving}>
              {saving ? 'Saving photo…' : 'Confirm & Start Delivery'}
            </button>
            {!can && (
              <p className="pu-ia2" style={{ textAlign: 'center', color: '#999', fontSize: 11, margin: '0.1rem' }}>
                {view === 'collect' ? 'Take the package photo' : 'Complete checklist and package photo'}
              </p>
            )}
            {view === 'checklist' && mayReport ? (
              <button type="button" className="ps-link" onClick={() => setMode('report')}>
                Something's wrong with the package
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
